import React, { useState } from 'react';
import { User, LogOut, Menu, X, Bell, KeyboardIcon } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { NotificationModal } from '../modals/NotificationModal';
import { USER_ROLES } from '@/utils/constants';
import logo from '@/assets/Logomark@2x.png' ;
import { KeyboardShortcutsHelp } from '../keyboard';

export const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
const [showHelpRequiredModal, setShowHelpRequiredModal] = useState(false);

  // Role-based menu visibility
  const isAdmin = user?.role === USER_ROLES.ADMIN;
  const isTeamLeader = user?.role === USER_ROLES.TEAM_MEMBER;
  const canViewTeamTasks = isAdmin || isTeamLeader;
  const canViewAllTasks = isAdmin;
  const canViewInvoicing = isAdmin || isTeamLeader;
  const canViewConfigurations = isAdmin;

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  return (
    <header className="bg-[#162456] text-white sticky top-0 z-50 px-0.5 sm:px-3 py-2">
      <div className="flex items-center justify-between">
        {/* Logo and Desktop Navigation - KEEP ORIGINAL DESKTOP LAYOUT */}
        <div className="flex items-center">
          {/* Mobile menu button - Only show on mobile/tablet */}
          <button
            className="lg:hidden mr-3 p-1"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Logo - Keep original */}
          <div className="flex items-center space-x-2 mr-6">
            <div className="w-6 h-6 flex items-center justify-center">
              {/* <span className="text-white font-bold">T</span> */}
              <img src={logo} alt="" />
            </div>
           
          </div>
 <div className='border-r border-[#5C6589] h-4'></div>
          {/* Desktop Navigation - Keep original classes for large screens */}
          <nav className="hidden lg:flex items-center space-x-1">
            <NavItem 
              active={isActive('/tasks')}
              icon={<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M10.2001 0.599976H3.8001C2.0353 0.599976 0.600098 2.03518 0.600098 3.79998V10.2C0.600098 11.9648 2.0353 13.4 3.8001 13.4H10.2001C11.9649 13.4 13.4001 11.9648 13.4001 10.2V3.79998C13.4001 2.03518 11.9649 0.599976 10.2001 0.599976ZM10.4273 5.09678L6.6273 9.89678C6.4809 10.0816 6.2609 10.1928 6.0249 10.2C6.0169 10.2 6.0081 10.2 6.0001 10.2C5.7737 10.2 5.5569 10.104 5.4057 9.93518L3.6057 7.93518C3.3105 7.60638 3.3369 7.10078 3.6649 6.80558C3.9937 6.50958 4.4985 6.53678 4.7945 6.86478L5.9609 8.16078L9.1729 4.10398C9.4473 3.75838 9.9497 3.69918 10.2969 3.97278C10.6433 4.24718 10.7017 4.75038 10.4273 5.09678Z" fill="white"/>
</svg>

              }
              text="My Tasks" 
              onClick={() => navigate('/tasks')}
            />
            
            {canViewTeamTasks && (
              <NavItem 
                active={isActive('/team-tasks')}
                icon={
                 <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<g opacity="0.4">
<path d="M2.3999 4.79999L3.5999 5.99999L6.3999 2.79999" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M4.7999 9.59998H3.1999C2.75807 9.59998 2.3999 9.95815 2.3999 10.4V12C2.3999 12.4418 2.75807 12.8 3.1999 12.8H4.7999C5.24173 12.8 5.5999 12.4418 5.5999 12V10.4C5.5999 9.95815 5.24173 9.59998 4.7999 9.59998Z" fill="white" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M8.7998 4.79999H13.5998" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M8.7998 11.2H13.5998" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</g>
</svg>

                }
                text="My Team Tasks" 
                onClick={() => navigate('/team-tasks')}
              />
            )}
            
            {canViewAllTasks && (
              <NavItem 
                active={isActive('/all-tasks')}
                icon={
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<g opacity="0.4">
<path d="M5.1999 2.40002H3.5999C2.93716 2.40002 2.3999 2.93728 2.3999 3.60002V5.20002C2.3999 5.86277 2.93716 6.40002 3.5999 6.40002H5.1999C5.86264 6.40002 6.3999 5.86277 6.3999 5.20002V3.60002C6.3999 2.93728 5.86264 2.40002 5.1999 2.40002Z" fill="white" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M5.1999 9.60004H3.5999C2.93716 9.60004 2.3999 10.1373 2.3999 10.8V12.4C2.3999 13.0628 2.93716 13.6 3.5999 13.6H5.1999C5.86264 13.6 6.3999 13.0628 6.3999 12.4V10.8C6.3999 10.1373 5.86264 9.60004 5.1999 9.60004Z" fill="white" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M12.4001 9.60004H10.8001C10.1374 9.60004 9.6001 10.1373 9.6001 10.8V12.4C9.6001 13.0628 10.1374 13.6 10.8001 13.6H12.4001C13.0628 13.6 13.6001 13.0628 13.6001 12.4V10.8C13.6001 10.1373 13.0628 9.60004 12.4001 9.60004Z" fill="white" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M9.19971 4.80002L10.7997 6.40002L13.9997 2.40002" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</g>
</svg>


                }
                text="All Tasks" 
                onClick={() => navigate('/all-tasks')}
              />
            )}
            
            {canViewInvoicing && (
              <NavItem 
                active={isActive('/invoicing')}
                icon={
                 <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<g opacity="0.4">
<path d="M11.1999 1.59998H4.7999C3.47438 1.59998 2.3999 2.67446 2.3999 3.99998V13.6C2.3999 13.8949 2.56238 14.166 2.82254 14.3054C3.0819 14.4445 3.3991 14.4289 3.64366 14.2656L5.5999 12.9613L7.55614 14.2656C7.82486 14.4447 8.17494 14.4447 8.44366 14.2656L10.3999 12.9613L12.3561 14.2656C12.4897 14.355 12.6445 14.4 12.7999 14.4C12.9296 14.4 13.0593 14.3687 13.1773 14.3054C13.4374 14.166 13.5999 13.8949 13.5999 13.6V3.99998C13.5999 2.67446 12.5254 1.59998 11.1999 1.59998ZM7.5999 10.4H5.5999C5.15774 10.4 4.7999 10.0417 4.7999 9.59998C4.7999 9.15822 5.15774 8.79998 5.5999 8.79998H7.5999C8.04206 8.79998 8.3999 9.15822 8.3999 9.59998C8.3999 10.0417 8.04206 10.4 7.5999 10.4ZM7.5999 7.19998H5.5999C5.15774 7.19998 4.7999 6.84174 4.7999 6.39998C4.7999 5.95822 5.15774 5.59998 5.5999 5.59998H7.5999C8.04206 5.59998 8.3999 5.95822 8.3999 6.39998C8.3999 6.84174 8.04206 7.19998 7.5999 7.19998ZM10.3999 10.4C9.95806 10.4 9.5999 10.0418 9.5999 9.59998C9.5999 9.15814 9.95806 8.79998 10.3999 8.79998C10.8417 8.79998 11.1999 9.15814 11.1999 9.59998C11.1999 10.0418 10.8417 10.4 10.3999 10.4ZM10.3999 7.19998C9.95806 7.19998 9.5999 6.84182 9.5999 6.39998C9.5999 5.95814 9.95806 5.59998 10.3999 5.59998C10.8417 5.59998 11.1999 5.95814 11.1999 6.39998C11.1999 6.84182 10.8417 7.19998 10.3999 7.19998Z" fill="white"/>
</g>
</svg>

                }
                text="Quotations & Invoicing" 
                onClick={() => navigate('/invoicing')}
              />
            )}
            
            <NavItem 
              active={isActive('/reports')}
              icon={
               <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<g opacity="0.4">
<path d="M8.39971 3.20001H7.59971C7.37879 3.20001 7.19971 3.3791 7.19971 3.60001V12.4C7.19971 12.6209 7.37879 12.8 7.59971 12.8H8.39971C8.62062 12.8 8.79971 12.6209 8.79971 12.4V3.60001C8.79971 3.3791 8.62062 3.20001 8.39971 3.20001Z" fill="white" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M3.5999 7.20001H2.7999C2.57899 7.20001 2.3999 7.3791 2.3999 7.60001V12.4C2.3999 12.6209 2.57899 12.8 2.7999 12.8H3.5999C3.82082 12.8 3.9999 12.6209 3.9999 12.4V7.60001C3.9999 7.3791 3.82082 7.20001 3.5999 7.20001Z" fill="white" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M13.2 10.4H12.4C12.1791 10.4 12 10.5791 12 10.8V12.4C12 12.6209 12.1791 12.8 12.4 12.8H13.2C13.4209 12.8 13.6 12.6209 13.6 12.4V10.8C13.6 10.5791 13.4209 10.4 13.2 10.4Z" fill="white" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</g>
</svg>

              }
              text="Reports" 
              onClick={() => navigate('/reports')}
            />
            
            {canViewConfigurations && (
              <NavItem 
                active={isActive('/configurations')}
                icon={
                 <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<g opacity="0.4">
<path d="M13.9578 6.88398L12.9354 6.37278C12.8546 6.12718 12.7538 5.89118 12.6386 5.66318L13.001 4.57598C13.097 4.28878 13.0218 3.97118 12.8082 3.75758L12.2426 3.19198C12.029 2.97758 11.7114 2.90238 11.4242 2.99838L10.3362 3.36078C10.1082 3.24558 9.87223 3.14558 9.62663 3.06398L9.11543 2.04158C8.97943 1.77038 8.70263 1.59918 8.39943 1.59918H7.59943C7.29623 1.59918 7.01943 1.77038 6.88343 2.04158L6.37223 3.06398C6.12663 3.14478 5.89063 3.24558 5.66263 3.36078L4.57463 2.99838C4.28743 2.90238 3.96983 2.97758 3.75623 3.19198L3.19063 3.75758C2.97623 3.97198 2.90183 4.28878 2.99783 4.57598L3.36023 5.66318C3.24503 5.89118 3.14503 6.12718 3.06343 6.37278L2.04103 6.88398C1.76983 7.01918 1.59863 7.29678 1.59863 7.59998V8.39998C1.59863 8.70318 1.76983 8.97998 2.04103 9.11598L3.06343 9.62718C3.14423 9.87278 3.24503 10.1088 3.36023 10.3368L2.99783 11.424C2.90183 11.7112 2.97703 12.0288 3.19063 12.2424L3.75623 12.808C3.90823 12.9608 4.11303 13.0424 4.32183 13.0424C4.40583 13.0424 4.49223 13.0288 4.57463 13.0016L5.66263 12.6392C5.89063 12.7544 6.12663 12.8544 6.37223 12.936L6.88343 13.9584C7.01943 14.2296 7.29623 14.4008 7.59943 14.4008H8.39943C8.70263 14.4008 8.97943 14.2296 9.11543 13.9584L9.62663 12.936C9.87223 12.8552 10.1082 12.7544 10.3362 12.6392L11.4242 13.0016C11.5074 13.0296 11.593 13.0424 11.677 13.0424C11.8858 13.0424 12.0906 12.96 12.2426 12.808L12.8082 12.2424C13.0226 12.028 13.097 11.7112 13.001 11.424L12.6386 10.3368C12.7538 10.1088 12.8538 9.87278 12.9354 9.62718L13.9578 9.11598C14.229 8.98078 14.4002 8.70318 14.4002 8.39998V7.59998C14.4002 7.29678 14.229 7.01998 13.9578 6.88398ZM8.00023 10.4C6.67463 10.4 5.60023 9.32558 5.60023 7.99998C5.60023 6.67438 6.67463 5.59998 8.00023 5.59998C9.32583 5.59998 10.4002 6.67438 10.4002 7.99998C10.4002 9.32558 9.32583 10.4 8.00023 10.4Z" fill="white"/>
</g>
</svg>

                }
                text="Configurations" 
                onClick={() => navigate('/configurations')}
              />
            )}
          </nav>
        </div>

        {/* Right side - User menu - Keep original but make responsive for tablet */}
        <div className="flex items-center space-x-2 sm:space-x-2">
           <div className="cursor-pointer hover:text-blue-500" >
                      <KeyboardIcon size={16} onClick={()=>setShowHelpRequiredModal(true)} />
                      </div>
          {/* Notification Bell - Add for tablet/mobile */}
          <button
            onClick={() => setShowNotifications(true)}
            className="lg:hidden p-2 rounded-md hover:bg-[#2a2d47] transition-colors"
            aria-label="Notifications"
          >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M13.5999 10.8C12.7162 10.8 11.9999 10.0837 11.9999 9.20002V6.40002C11.9999 4.1909 10.209 2.40002 7.9999 2.40002C5.79078 2.40002 3.9999 4.1909 3.9999 6.40002V9.20002C3.9999 10.0837 3.28358 10.8 2.3999 10.8H13.5999Z" fill="white" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M6.04004 12.8C6.2254 13.7129 7.03212 14.4 7.99972 14.4C8.96732 14.4 9.77404 13.7129 9.9594 12.8H6.04004Z" fill="white"/>
</svg>

          </button>

          <div className="hidden sm:block text-xs">
            {/* Keep original welcome text hidden on mobile */}
          </div>

          <NotificationModal
            showNotifications={showNotifications} 
            setShowNotifications={setShowNotifications} 
          />

          {/* Profile button - Adjust for tablet */}
          <button 
            onClick={() => navigate('/profile')}
            className="flex items-center space-x-2 hover:bg-[#455078] px-2 sm:px-3 py-1.5 rounded transition-colors"
          >
           <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M8.0001 1.59998C4.4713 1.59998 1.6001 4.47118 1.6001 7.99998C1.6001 11.5288 4.4713 14.4 8.0001 14.4C11.5289 14.4 14.4001 11.5288 14.4001 7.99998C14.4001 4.47118 11.5289 1.59998 8.0001 1.59998ZM8.0001 4.79998C9.1025 4.79998 10.0001 5.69758 10.0001 6.79998C10.0001 7.90238 9.1025 8.79998 8.0001 8.79998C6.8977 8.79998 6.0001 7.90238 6.0001 6.79998C6.0001 5.69758 6.8977 4.79998 8.0001 4.79998ZM8.0001 12.8C6.7825 12.8 5.6737 12.3408 4.8265 11.5912C5.5625 10.6088 6.7153 9.99998 8.0001 9.99998C9.2849 9.99998 10.4385 10.6088 11.1737 11.5912C10.3265 12.3408 9.2169 12.8 8.0001 12.8Z" fill="white"/>
</svg>

            <span className="text-xs hidden md:inline">Profile</span>
          </button>
          
          {/* Logout button - Adjust for tablet */}
          <button 
            onClick={logout}
            className="flex items-center space-x-2 hover:bg-[#455078] px-2 sm:px-1 py-1.5 rounded text-red-400 transition-colors"
          >
           <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M5.5999 11.2L2.3999 8.00005L5.5999 4.80005" stroke="#FB7185" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M9.5999 8H2.3999" stroke="#FB7185" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M8.7998 2.40002H11.1998C12.5254 2.40002 13.5998 3.47442 13.5998 4.80002V11.2C13.5998 12.5256 12.5254 13.6 11.1998 13.6H8.7998" stroke="#FB7185" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>

            <span className="text-xs hidden md:inline">Logout</span>
          </button>
        </div>
      </div>

      {/* Mobile Navigation - Improved for tablet */}
      {mobileMenuOpen && (
        <nav className="lg:hidden absolute top-full left-0 w-full bg-[#1e2139] border-t border-gray-700 z-50 px-4 py-3 space-y-2 shadow-lg">
              {/* className="lg:hidden fixed inset-0 top-[64px] bg-[#1e2139] z-50 px-4 py-3 space-y-2 overflow-y-auto" */}

          {/* User info for mobile/tablet */}
          <div className="mb-3 pb-3 border-b border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-white font-medium">{user?.name}</p>
                <p className="text-gray-400 text-xs capitalize">
                  {user?.role?.replace('_', ' ')}
                </p>
              </div>
            </div>
          </div>
          
          {/* Navigation items with better tablet spacing */}
          <MobileNavItem text="My Tasks" onClick={() => { navigate('/tasks'); setMobileMenuOpen(false); }} />
          {canViewTeamTasks && (
            <MobileNavItem text="My Team Tasks" onClick={() => { navigate('/team-tasks'); setMobileMenuOpen(false); }} />
          )}
          {canViewAllTasks && (
            <MobileNavItem text="All Tasks" onClick={() => { navigate('/all-tasks'); setMobileMenuOpen(false); }} />
          )}
          {canViewInvoicing && (
            <MobileNavItem text="Quotations & Invoicing" onClick={() => { navigate('/invoicing'); setMobileMenuOpen(false); }} />
          )}
          <MobileNavItem text="Reports" onClick={() => { navigate('/reports'); setMobileMenuOpen(false); }} />
          {canViewConfigurations && (
            <MobileNavItem text="Configurations" onClick={() => { navigate('/configurations'); setMobileMenuOpen(false); }} />
          )}

          {/* Additional mobile actions */}
          <div className="pt-3 border-t border-gray-700 space-y-2">
            <MobileNavItem 
              text="Notifications" 
              onClick={() => { setShowNotifications(true); setMobileMenuOpen(false); }}
            />
            <MobileNavItem 
              text="Profile Settings" 
              onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }}
            />
            <MobileNavItem 
              text="Logout" 
              destructive={true}
              onClick={() => { logout(); setMobileMenuOpen(false); }}
            />
          </div>
        </nav>
      )}
      {showHelpRequiredModal && <KeyboardShortcutsHelp isOpen={showHelpRequiredModal} onClose={() => setShowHelpRequiredModal(false)}  />}
    </header>
  );
};

interface NavItemProps {
  active?: boolean;
  icon: React.ReactNode;
  text: string;
  onClick: () => void;
}

// Keep original NavItem component
const NavItem: React.FC<NavItemProps> = ({ active, icon, text, onClick }) => (
  <button
    onClick={onClick}
    className={`
      flex items-center space-x-2 px-4 py-2 rounded-md text-xs transition-colors
      ${active 
        ? 'bg-[#455078] text-white' 
        : 'text-[#A2A7BC] hover:text-white hover:bg-[#455078]'
      }
    `}
  >
    {icon}
    <span>{text}</span>
  </button>
);

// Enhanced MobileNavItem for better tablet experience
const MobileNavItem: React.FC<{ text: string; onClick: () => void; destructive?: boolean }> = ({ 
  text, 
  onClick, 
  destructive = false 
}) => (
  <button
    onClick={onClick}
    className={`
      block w-full text-left px-3 py-3 text-xs rounded-lg transition-colors
      ${destructive 
        ? 'text-red-400 hover:bg-red-400 hover:bg-opacity-10' 
        : 'text-gray-300 hover:text-white hover:bg-[#2a2d47]'
      }
    `}
  >
    {text}
  </button>
);

